import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { IoMdSend, IoMdCreate } from "react-icons/io";
import { HiSparkles } from "react-icons/hi";

// ==========================================
// CONSTANTS & CONFIG
// ==========================================
const API_ENDPOINTS = {
  WEBSITE_INFO: "https://api.confidanto.com/project-settings/get-website-info",
  GENERATE_AD_COPY:
    "https://api.confidanto.com/gemini/insight-2/generate-ad-copy",
};

const GENERIC_PROMPTS = [
  "please suggest ad copies",
  "generate ad copies",
  "suggest ad",
  "create ad",
  "write ad copy",
  "give me ad copy",
  "i need ad copies",
];

// ==========================================
// PURE UTILITY FUNCTIONS
// ==========================================
const sanitizeWords = (text = "") =>
  text
    .toLowerCase()
    .replace(/[^\w\s]/g, "")
    .split(/\s+/)
    .filter(Boolean);

const isQueryRelatedToBrand = (query, brandInfo) => {
  if (!brandInfo) return true;
  const brandWords = sanitizeWords(brandInfo);
  const queryWords = sanitizeWords(query);
  const matchCount = queryWords.filter((word) =>
    brandWords.includes(word),
  ).length;
  return matchCount >= 2;
};

const formatAdResponse = (headlines = [], descriptions = []) => {
  return headlines
    .map(
      (headline, idx) =>
        `Headline ${idx + 1}: ${headline}\n💬 ${descriptions[idx] || "—"}`,
    )
    .join("\n\n");
};

// ==========================================
// MAIN COMPONENT
// ==========================================
export default function AdCrafting() {
  // --- States ---
  const [query, setQuery] = useState("");
  const [brandInfo, setBrandInfo] = useState("");
  const [chatHistory, setChatHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [pendingPrompt, setPendingPrompt] = useState("");
  const [rejectedQuery, setRejectedQuery] = useState("");
  const [awaitingConfirmation, setAwaitingConfirmation] = useState(false);
  const [errors, setErrors] = useState({ query: false });

  // --- Refs ---
  const chatEndRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const typingSpeedRef = useRef([]);

  // --- Effects ---
  // 1. Fetch Brand Info on mount
  useEffect(() => {
    const fetchBrandInfo = async () => {
      try {
        const projectId = localStorage.getItem("project_id");
        if (!projectId) return;

        const res = await axios.post(API_ENDPOINTS.WEBSITE_INFO, { projectId });
        if (
          Array.isArray(res.data) &&
          res.data.length > 0 &&
          res.data[0]?.info
        ) {
          setBrandInfo(res.data[0].info.toLowerCase());
        }
      } catch (err) {
        console.error("Failed to fetch brand info:", err);
      }
    };

    fetchBrandInfo();

    return () => {
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    };
  }, []);

  // 2. Auto-scroll on new messages or loading state
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [chatHistory, isLoading]);

  // --- Validation ---
  const validateFields = () => {
    const isQueryEmpty = query.trim() === "";
    setErrors({ query: isQueryEmpty });
    return !isQueryEmpty;
  };

  // --- API Handlers ---
  const fetchAdCopies = async (prompt) => {
    setIsLoading(true);
    try {
      const res = await axios.post(API_ENDPOINTS.GENERATE_AD_COPY, {
        customer_id: localStorage.getItem("customer_id"),
        promptContext: prompt,
        project_id: localStorage.getItem("project_id"),
      });

      const formattedResponse = formatAdResponse(
        res.data.headlines || [],
        res.data.descriptions || [],
      );

      setChatHistory((prev) => [
        ...prev,
        {
          sender: "ai",
          message: `Here are your ad suggestions:\n\n${formattedResponse}`,
        },
      ]);
    } catch (err) {
      console.error("API Error:", err);
      setChatHistory((prev) => [
        ...prev,
        {
          sender: "ai",
          message: "Sorry, something went wrong. Please try again later.",
        },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  // --- Action Handlers ---
  const handleSearch = async () => {
    if (!validateFields()) return;

    let trimmedQuery = query.trim();
    const lowerQuery = trimmedQuery.toLowerCase();

    // Check generic prompts that need follow-up
    const isGeneric = GENERIC_PROMPTS.some((phrase) =>
      lowerQuery.includes(phrase),
    );
    if (isGeneric && !lowerQuery.includes("for ")) {
      setChatHistory((prev) => [
        ...prev,
        { sender: "user", message: trimmedQuery },
        {
          sender: "ai",
          message:
            "Sure! Please provide the product or subject you'd like ad copies for.",
        },
      ]);
      setPendingPrompt(trimmedQuery);
      setQuery("");
      return;
    }

    // Combine pending prompt if available
    if (pendingPrompt) {
      trimmedQuery = `${pendingPrompt} for ${trimmedQuery}`;
      setPendingPrompt("");
    }

    setChatHistory((prev) => [
      ...prev,
      { sender: "user", message: trimmedQuery },
    ]);
    setQuery("");

    // Check brand relevance
    if (!isQueryRelatedToBrand(trimmedQuery, brandInfo)) {
      setChatHistory((prev) => [
        ...prev,
        {
          sender: "ai",
          message:
            "Sure! Please provide the product or subject you'd like ad copies for. If you'd still like to continue with this prompt, click below.",
        },
      ]);
      setAwaitingConfirmation(true);
      setRejectedQuery(trimmedQuery);
      return;
    }

    await fetchAdCopies(trimmedQuery);
  };

  const handleConfirmContinue = async () => {
    setAwaitingConfirmation(false);
    await fetchAdCopies(rejectedQuery);
    setRejectedQuery("");
  };

  // --- Input & Fast Typing Detection ---
  const handleSearchQueryChange = (e) => {
    const newValue = e.target.value;
    setQuery(newValue);

    if (newValue.trim() !== "") {
      setErrors({ query: false });
    }

    // Track typing speed
    const now = Date.now();
    typingSpeedRef.current.push(now);
    if (typingSpeedRef.current.length > 5) {
      typingSpeedRef.current.shift();
    }

    clearTimeout(typingTimeoutRef.current);
    typingTimeoutRef.current = setTimeout(() => {
      const timestamps = typingSpeedRef.current;
      if (timestamps.length >= 5) {
        const typingDuration =
          timestamps[timestamps.length - 1] - timestamps[0];
        if (typingDuration < 600) {
          handleSearch();
          typingSpeedRef.current = [];
        }
      }
    }, 500);
  };

  return (
    <div className="flex flex-col h-[calc(100vh-4rem)] bg-slate-50 overflow-hidden">
      {/* Header */}
      <Header brandInfoActive={Boolean(brandInfo)} />

      {/* Chat Area */}
      <div className="flex-1 overflow-y-auto px-4 py-6 space-y-8 bg-slate-50 scroll-smooth">
        {chatHistory.length === 0 ? (
          <EmptyState />
        ) : (
          <div className="max-w-4xl mx-auto space-y-6">
            {chatHistory.map((chat, idx) => (
              <ChatMessage
                key={idx}
                message={chat.message}
                isUser={chat.sender === "user"}
              />
            ))}
            {isLoading && <TypingIndicator />}
            <div ref={chatEndRef} />
          </div>
        )}
      </div>

      {/* Input Area */}
      <InputSection
        query={query}
        setQuery={setQuery}
        isLoading={isLoading}
        errors={errors}
        pendingPrompt={pendingPrompt}
        rejectedQuery={rejectedQuery}
        awaitingConfirmation={awaitingConfirmation}
        onInputChange={handleSearchQueryChange}
        onSubmit={handleSearch}
        onConfirmContinue={handleConfirmContinue}
      />
    </div>
  );
}

// ==========================================
// SUB-COMPONENTS
// ==========================================

function Header({ brandInfoActive }) {
  return (
    <header className="flex-none px-6 py-4 bg-white border-b border-gray-200 flex justify-between items-center shadow-sm z-20">
      <div className="flex items-center gap-3">
        <div className="p-2 bg-indigo-50 rounded-lg text-indigo-600">
          <IoMdCreate size={20} />
        </div>
        <div>
          <h2 className="text-lg font-bold text-gray-900 tracking-tight">
            Ad Copy Generator
          </h2>
          <p className="text-xs text-gray-500 font-medium">
            AI-Powered Marketing Assistant
          </p>
        </div>
      </div>

      {brandInfoActive && (
        <div className="flex items-center gap-2 px-3 py-1.5 bg-emerald-50 text-emerald-700 text-xs font-semibold rounded-full border border-emerald-100 shadow-sm">
          <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
          Brand Context Active
        </div>
      )}
    </header>
  );
}

function EmptyState() {
  return (
    <div className="h-full flex flex-col items-center justify-center text-center px-4">
      <div className="w-20 h-20 bg-white rounded-2xl shadow-sm border border-gray-100 flex items-center justify-center mb-6 text-indigo-500">
        <HiSparkles size={32} />
      </div>
      <h3 className="text-xl font-semibold text-gray-800 mb-2">
        Ready to create ads?
      </h3>
      <p className="text-gray-500 max-w-md text-sm leading-relaxed">
        Start by typing your product name or topic below. I'll generate
        compelling headlines and descriptions tailored to your brand.
      </p>
    </div>
  );
}

function ChatMessage({ message, isUser }) {
  return (
    <div className={`flex w-full ${isUser ? "justify-end" : "justify-start"}`}>
      <div
        className={`flex max-w-[90%] md:max-w-[80%] gap-4 ${isUser ? "flex-row-reverse" : "flex-row"}`}
      >
        {/* Avatar */}
        <div
          className={`w-9 h-9 rounded-full flex-shrink-0 flex items-center justify-center text-xs font-bold border ${
            isUser
              ? "bg-blue-600 text-white border-blue-600"
              : "bg-white text-indigo-600 border-gray-200 shadow-sm"
          }`}
        >
          {isUser ? "You" : "AI"}
        </div>

        {/* Bubble */}
        <div
          className={`px-6 py-4 text-sm leading-relaxed shadow-sm whitespace-pre-wrap ${
            isUser
              ? "bg-blue-600 text-gray-50 rounded-2xl rounded-tr-sm"
              : "bg-white text-gray-800 border border-gray-200 rounded-2xl rounded-tl-sm"
          }`}
        >
          {message}
        </div>
      </div>
    </div>
  );
}

function TypingIndicator() {
  return (
    <div className="flex w-full justify-start">
      <div className="flex max-w-[85%] gap-4">
        <div className="w-9 h-9 rounded-full bg-white text-indigo-600 border border-gray-200 shadow-sm flex-shrink-0 flex items-center justify-center text-xs font-bold">
          AI
        </div>
        <div className="bg-white border border-gray-200 px-6 py-4 rounded-2xl rounded-tl-none shadow-sm flex items-center space-x-2">
          <span className="w-2 h-2 bg-indigo-400 rounded-full animate-bounce" />
          <span
            className="w-2 h-2 bg-indigo-400 rounded-full animate-bounce"
            style={{ animationDelay: "150ms" }}
          />
          <span
            className="w-2 h-2 bg-indigo-400 rounded-full animate-bounce"
            style={{ animationDelay: "300ms" }}
          />
        </div>
      </div>
    </div>
  );
}

function InputSection({
  query,
  isLoading,
  errors,
  pendingPrompt,
  rejectedQuery,
  awaitingConfirmation,
  onInputChange,
  onSubmit,
  onConfirmContinue,
}) {
  return (
    <div className="flex-none p-6 bg-white border-t border-gray-200">
      <div className="max-w-4xl mx-auto flex flex-col gap-4">
        {/* Brand Mismatch Alert */}
        {awaitingConfirmation && rejectedQuery && (
          <div className="flex items-center justify-between p-4 bg-amber-50 border border-amber-200 rounded-xl text-amber-900 text-sm shadow-sm">
            <div className="flex items-center gap-2">
              <span className="text-xl">⚠️</span>
              <span>
                This topic <strong>"{rejectedQuery}"</strong> seems unrelated to
                your brand. Proceed anyway?
              </span>
            </div>
            <button
              onClick={onConfirmContinue}
              className="px-4 py-1.5 bg-amber-200 hover:bg-amber-300 rounded-lg text-amber-900 font-semibold text-xs transition-colors"
            >
              Continue Generation
            </button>
          </div>
        )}

        {/* Pending Context Badge */}
        {pendingPrompt && (
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-50 text-indigo-700 text-xs font-medium rounded-lg border border-indigo-100 self-start animate-pulse">
            <span>Waiting for context regarding:</span>
            <strong>{pendingPrompt}</strong>
          </div>
        )}

        {/* Input Bar */}
        <div className="relative group">
          <input
            type="text"
            value={query}
            onChange={onInputChange}
            placeholder={
              pendingPrompt
                ? "e.g., Organic Hair Oil"
                : "Ask for an ad copy (e.g., 'Face cream for summer')"
            }
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                onSubmit();
              }
            }}
            className={`w-full pl-5 pr-14 py-4 bg-white border shadow-sm focus:shadow-md rounded-xl transition-all outline-none text-gray-800 placeholder-gray-400 text-sm ${
              errors.query
                ? "border-red-300 focus:border-red-500 focus:ring-4 focus:ring-red-50"
                : "border-gray-200 focus:border-indigo-500 focus:ring-4 focus:ring-indigo-50/50"
            }`}
          />
          <button
            onClick={onSubmit}
            disabled={isLoading}
            className={`absolute right-2 top-1/2 -translate-y-1/2 p-2.5 rounded-lg transition-all duration-200 ${
              isLoading || (!query.trim() && !pendingPrompt)
                ? "text-gray-300 bg-transparent cursor-default"
                : "bg-indigo-600 text-white hover:bg-indigo-700 shadow-sm hover:shadow-md transform active:scale-95"
            }`}
          >
            <IoMdSend size={18} />
          </button>
        </div>

        {errors.query && (
          <p className="text-red-500 text-xs font-medium ml-2 animate-pulse">
            Please enter a topic to start generating ads.
          </p>
        )}
      </div>
    </div>
  );
}
